import { KeyRound, Wifi, ShieldCheck, Sparkles } from "lucide-react";

/**
 * ⚠️ 10Web AI WARNING — DO NOT REPLACE <Icon /> JSX with <img> tags.
 * All lucide icons here are wrapped with data-icon-container="lucide-do-not-replace".
 * Keep them as React components.
 */

const NAVY = "#081438";
const NAVY_LIGHT = "#243752";
const GOLD = "#F5A623";
const GOLD_DARK = "#9A6400";
const CREAM = "#FAF7F1";
const CREAM_DEEP = "#F0EBE0";
const INK = "#1A1F2E";
const INK_SOFT = "#5A6478";
const FONT_HEAD = "'Plus Jakarta Sans', system-ui, sans-serif";
const FONT_BODY = "'Manrope', system-ui, sans-serif";
const FACT_ROWS = [{
  Icon: KeyRound,
  label: "Remotes synced this year",
  value: "1,400+"
}, {
  Icon: Wifi,
  label: "Brands programmed in-house",
  value: "12"
}, {
  Icon: ShieldCheck,
  label: "Rolling-code security",
  value: "Standard"
}, {
  Icon: Sparkles,
  label: "Avg. visit time",
  value: "20 min"
}];
export default function RemoteIntro() {
  return <section className="relative overflow-hidden" style={{
    backgroundColor: CREAM,
    fontFamily: FONT_BODY,
    paddingTop: "100px",
    paddingBottom: "100px"
  }}>
      {/* Paper grain */}
      <div aria-hidden className="absolute inset-0 opacity-[0.04] pointer-events-none" style={{
      backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='200'%3E%3Cfilter id='n'%3E%3CfeTurbulence baseFrequency='0.85' numOctaves='2'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.5'/%3E%3C/svg%3E\")"
    }} />

      {/* Corner gold ornaments */}
      <div aria-hidden className="absolute top-10 left-10 pointer-events-none" style={{
      opacity: 0.55
    }}>
        <svg width="60" height="60" viewBox="0 0 60 60" fill="none">
          <path d="M2 2 L2 30 M2 2 L30 2" stroke={GOLD} strokeWidth="2" strokeLinecap="square" />
          <circle cx="2" cy="2" r="3" fill={GOLD} />
        </svg>
      </div>
      <div aria-hidden className="absolute bottom-10 right-10 pointer-events-none" style={{
      opacity: 0.55,
      transform: "rotate(180deg)"
    }}>
        <svg width="60" height="60" viewBox="0 0 60 60" fill="none">
          <path d="M2 2 L2 30 M2 2 L30 2" stroke={GOLD} strokeWidth="2" strokeLinecap="square" />
          <circle cx="2" cy="2" r="3" fill={GOLD} />
        </svg>
      </div>

      <div className="relative max-w-[1240px] mx-auto px-6 lg:px-10">
        {/* Section label */}
        <div className="mb-10 flex items-center gap-3">
          <div aria-hidden className="h-px w-12" style={{
          backgroundColor: GOLD
        }} />
          <span style={{
          fontFamily: FONT_HEAD,
          fontSize: "12px",
          fontWeight: 700,
          letterSpacing: "0.22em",
          textTransform: "uppercase",
          color: GOLD_DARK
        }}>
            02 · The Quick Truth About Remotes
          </span>
        </div>

        <div className="grid lg:grid-cols-[1.4fr_1fr] gap-14 lg:gap-20 items-start">
          {/* LEFT — editorial copy */}
          <div>
            <h2 style={{
            fontFamily: FONT_HEAD,
            fontWeight: 800,
            fontSize: "clamp(2.1rem, 4vw, 3.4rem)",
            lineHeight: 1.08,
            letterSpacing: "-0.025em",
            color: NAVY,
            marginBottom: "32px",
            maxWidth: "640px"
          }}>
              Most remotes aren't{" "}
              <span style={{
              position: "relative",
              display: "inline-block",
              color: NAVY
            }}>
                plug-and-play
                <span aria-hidden style={{
                position: "absolute",
                left: 0,
                right: 0,
                bottom: "0.08em",
                height: "0.18em",
                backgroundColor: `${GOLD}55`,
                zIndex: -1
              }} />
              </span>{" "}
              anymore.
            </h2>

            <div style={{
            fontFamily: FONT_BODY,
            fontSize: "17px",
            lineHeight: 1.75,
            color: INK,
            maxWidth: "620px"
          }}>
              <p style={{
              marginBottom: "18px"
            }}>
                <span style={{
                fontFamily: FONT_HEAD,
                fontSize: "5.5rem",
                fontWeight: 800,
                lineHeight: 0.85,
                color: GOLD,
                float: "left",
                marginRight: "14px",
                marginTop: "8px",
                marginBottom: "-8px",
                letterSpacing: "-0.04em"
              }}>
                  M
                </span>
                ost modern garage doors use{" "}
                <strong style={{
                color: NAVY
              }}>rolling-code</strong>{" "}
                technology — a new security signal generated every single press,
                so the door can't be cloned or replayed. Brilliant for security.
                A nightmare if you've never paired a remote before.
              </p>

              <p style={{
              marginBottom: "18px"
            }}>
                Every brand does it a little differently. B&amp;D uses Tri-Tran
                pulses. Merlin runs Security+ 2.0. Chamberlain has its own
                walk-through sequence. ATA uses its TrioCode 128 chip. Get one
                step out of order and the motor either ignores the remote — or
                worse, locks you out for an hour while it cools.
              </p>

              <p style={{
              marginBottom: 0
            }}>
                We bring the right remote for your motor (or a universal that
                matches), program it on-site, sync any keypads or car HomeLink
                buttons, and{" "}
                <strong style={{
                color: NAVY
              }}>
                  wipe old remotes from previous owners
                </strong>{" "}
                before we leave. You drive away with a door that knows you — and
                only you.
              </p>
            </div>
          </div>

          {/* RIGHT — by the numbers card */}
          <div className="relative rounded-2xl overflow-hidden" style={{
          backgroundColor: NAVY,
          boxShadow: "0 20px 60px rgba(8,20,56,0.25)"
        }}>
            {/* Top gold edge */}
            <div aria-hidden className="absolute top-0 left-0 right-0 h-1" style={{
            background: `linear-gradient(90deg, ${GOLD} 0%, ${GOLD_DARK} 100%)`
          }} />

            {/* Watermark */}
            <div aria-hidden className="absolute pointer-events-none select-none" style={{
            right: "-20px",
            bottom: "-40px",
            fontFamily: FONT_HEAD,
            fontSize: "180px",
            fontWeight: 800,
            lineHeight: 1,
            color: `${GOLD}10`,
            letterSpacing: "-0.05em"
          }}>
              02
            </div>

            <div className="p-8 md:p-10 relative">
              <div className="mb-6">
                <span style={{
                fontFamily: FONT_HEAD,
                fontSize: "11px",
                fontWeight: 700,
                letterSpacing: "0.22em",
                textTransform: "uppercase",
                color: GOLD
              }}>
                  By the numbers
                </span>
                <h3 style={{
                fontFamily: FONT_HEAD,
                fontWeight: 800,
                fontSize: "1.6rem",
                lineHeight: 1.15,
                color: "#ffffff",
                marginTop: "10px",
                letterSpacing: "-0.02em"
              }}>
                  What "programming" actually looks like.
                </h3>
              </div>

              <ul className="space-y-5">
                {FACT_ROWS.map(({
                Icon,
                label,
                value
              }) => <li key={label} className="flex items-center justify-between gap-4 pb-5 border-b" style={{
                borderColor: "rgba(255,255,255,0.08)"
              }}>
                    <div className="flex items-center gap-3">
                      <span aria-hidden data-icon-container="lucide-do-not-replace" className="inline-flex items-center justify-center rounded-md" style={{
                    width: 36,
                    height: 36,
                    backgroundColor: `${GOLD}18`
                  }}>
                        <Icon size={18} color={GOLD} strokeWidth={2.5} />
                      </span>
                      <span style={{
                    fontFamily: FONT_BODY,
                    fontSize: "14px",
                    color: "rgba(255,255,255,0.82)",
                    fontWeight: 500,
                    lineHeight: 1.35
                  }}>
                        {label}
                      </span>
                    </div>
                    <span style={{
                  fontFamily: FONT_HEAD,
                  fontSize: "20px",
                  fontWeight: 800,
                  color: GOLD,
                  letterSpacing: "-0.01em"
                }}>
                      {value}
                    </span>
                  </li>)}
              </ul>

              {/* Signature line */}
              <div className="mt-7 pt-6 border-t flex items-center gap-3" style={{
              borderColor: "rgba(255,255,255,0.12)"
            }}>
                <span style={{
                fontFamily: FONT_HEAD,
                fontSize: "10px",
                fontWeight: 800,
                letterSpacing: "0.18em",
                color: GOLD
              }}>
                  GG
                </span>
                <span style={{
                fontFamily: FONT_BODY,
                fontSize: "11px",
                color: "rgba(255,255,255,0.5)",
                letterSpacing: "0.05em"
              }}>
                  Bass Hill · Est. 2009 · Family-run
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}